import { useState, type FormEvent } from 'react';

/**
 * Shown only when the app is opened directly rather than embedded — i.e. there
 * is no `#api=` in the hash. In production the host panel always supplies it.
 * This exists so the tool can be driven against a betlens API by hand.
 *
 * What's entered here is NOT persisted anywhere — it's handed straight to the
 * app's in-memory state for the session. Reload, and the form returns; close the
 * tab, and the token is gone. That's intentional (the token is a short-lived JWT).
 */
export function BootstrapForm({
  onReady,
  initialApi,
  apiError,
}: {
  onReady: (v: { api: string; token: string }) => void;
  initialApi?: string | null;
  /** Why a supplied `api` did not resolve — never leave the user guessing. */
  apiError?: string | null;
}) {
  const [api, setApi] = useState(initialApi ?? '/betlens');
  const [token, setToken] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onReady({ api: api.trim(), token: token.trim() });
  };

  return (
    <div className="instrument-bg flex min-h-full items-center justify-center p-8">
      <form
        onSubmit={submit}
        className="w-full max-w-[440px] border border-hairline bg-surface p-6 rise"
      >
        <div className="spectral mb-1 font-condensed text-[18px] font-semibold uppercase tracking-[0.18em]">
          Betlens
        </div>
        {apiError ? (
          // An api WAS passed and we could not use it. Saying "not embedded"
          // here would send someone hunting for a missing parameter that is
          // present.
          <p className="mb-5 border-l-2 border-l-warn bg-warn-bg px-3 py-2 text-[12.5px] leading-relaxed text-text">
            {apiError}
          </p>
        ) : (
          <p className="mb-5 text-[12.5px] leading-relaxed text-text-dim">
            Not embedded — no bootstrap context in the URL. Point this at a betlens
            API to start. Inside an admin panel this step never appears; the panel
            passes it in the URL fragment.
          </p>
        )}

        <label className="mb-3 block">
          <span className="eyebrow mb-1 block">API base</span>
          <input
            value={api}
            onChange={(e) => setApi(e.target.value)}
            placeholder="/betlens"
            spellCheck={false}
            className="w-full border border-hairline bg-surface-sunken px-2 py-1.5 font-mono text-[12px] text-text focus:border-accent focus:outline-none"
          />
          <span className="mt-1 block text-[11px] text-text-faint">
            Use <code className="font-mono">/betlens</code> to go through the Vite dev proxy.
          </span>
        </label>

        <label className="mb-5 block">
          <span className="eyebrow mb-1 block">token (optional)</span>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="JWT — leave empty if the dev-server has no auth"
            spellCheck={false}
            className="w-full border border-hairline bg-surface-sunken px-2 py-1.5 font-mono text-[12px] text-text focus:border-accent focus:outline-none"
          />
        </label>

        <button
          type="submit"
          className="w-full border border-accent/40 bg-accent/10 py-2 font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent hover:bg-accent/20"
        >
          Connect
        </button>
      </form>
    </div>
  );
}
